import { DATE_LOCALE } from '@/lib/constants';

const MONTH_ABBR_PT_BR = [
  'jan', 'fev', 'mar', 'abr', 'mai', 'jun',
  'jul', 'ago', 'set', 'out', 'nov', 'dez',
] as const;

const MONTH_NAMES_PT_BR = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
] as const;

export const formatDate =(date: string): string => {
  const [year, month, day] = date.split('-').map(Number);
  const localDate = new Date(year, month - 1, day);
  return localDate.toLocaleDateString(DATE_LOCALE, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const formatDateShort = (date: string): string => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [year, month, day] = date.split('-').map(Number);
    const localDate = new Date(year, month - 1, day);
    return localDate.toLocaleDateString(DATE_LOCALE, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
  const parsedDate = new Date(date);
  return parsedDate.toLocaleDateString(DATE_LOCALE, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// "10/08/2026 – 05/09/2026": a range written the way the period bar writes it.
export const formatDateRange = (from: Date, to: Date): string => {
  const format = (date: Date) =>
    date.toLocaleDateString(DATE_LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' });
  return `${format(from)} – ${format(to)}`;
};

export const formatDateTime = (dateTime: string): string => {
  const parsedDate = new Date(dateTime);
  return parsedDate.toLocaleDateString(DATE_LOCALE, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const toDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const fromDateString = (value: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const [, y, m, d] = match;
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  return Number.isNaN(date.getTime()) ? null : date;
};

export type Period = { year: number; month: number };

export const formatMonthAbbr = (year: number, month: number): string =>
  `${MONTH_ABBR_PT_BR[month]}./${year}`;

export const formatPeriodLabel = (period: Period): string =>
  formatMonthAbbr(period.year, period.month);

// The month alone, for a sentence that starts with it ("Setembro: …"); the year is in the period bar above.
export const formatMonthName = (period: Period): string => MONTH_NAMES_PT_BR[period.month];

export const toPeriodString = (period: Period): string => {
  const month = String(period.month + 1).padStart(2, '0');
  return `${period.year}-${month}`;
};

export const parsePeriod = (value: string | null | undefined): Period | null => {
  if (!value) return null;
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(value);
  if (!match) return null;
  return { year: Number(match[1]), month: Number(match[2]) - 1 };
};

export const currentPeriod = (now: Date = new Date()): Period => ({
  year: now.getFullYear(),
  month: now.getMonth(),
});

export const monthBounds = (period: Period): { from: Date; to: Date } => ({
  from: new Date(period.year, period.month, 1),
  to: new Date(period.year, period.month + 1, 0),
});

export const periodFromDate = (date: Date): Period => ({
  year: date.getFullYear(),
  month: date.getMonth(),
});

export const shiftPeriod = (period: Period, delta: number): Period => {
  const date = new Date(period.year, period.month + delta, 1);
  return { year: date.getFullYear(), month: date.getMonth() };
};

export const samePeriod = (a: Period, b: Period): boolean =>
  a.year === b.year && a.month === b.month;

export const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();
